import React, { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  reportPages as reportPagesAtom,
  reportAvailableTypes as reportAvailableTypesAtom,
  reportForType as reportForTypeAtom,
  assessmentConfiguration as assessmentConfigurationAtom,
} from 'state/atoms';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { ReportPageItem, ReportSectionItem } from 'models/report';
import {
  Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row,
} from 'reactstrap';
import Button from 'components/common/Button';
import AssessmentGraph from 'components/assessment-result/AssessmentGraph';
import AssessmentType from 'components/assessment-result/AssessmentType';
import PromptModal from 'components/common/PromptModal';

interface Props {
  page: ReportPageItem;
  section: ReportSectionItem;
  pageIndex: number;
  sectionIndex: number;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  builderMode?: boolean;
  preview?: boolean;
}

const AssessmentResult: React.FC<Props> = ({
  page, section, pageIndex, sectionIndex, dragHandleProps, builderMode, preview,
}) => {
  const assessmentConfiguration = useRecoilValue(assessmentConfigurationAtom);

  const [reportPages, setReportPages] = useRecoilState(reportPagesAtom);
  const reportAvailableTypes = useRecoilValue(reportAvailableTypesAtom);
  const reportForType = useRecoilValue(reportForTypeAtom);
  const [editModal, setEditModal] = useState(false);
  const [newSectionName, setNewSectionName] = useState(section?.name);
  const [deleteModal, setDeleteModal] = useState(false);

  const toggleEditModal = () => setEditModal(!editModal);

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const deleteSection = () => {
    const newState = [...reportPages[pageIndex].sections];
    newState.splice(sectionIndex, 1);
    setReportPages(reportPages.map((item, i) => {
      if (i === pageIndex) {
        return { ...item, sections: newState };
      }
      return item;
    }));
  };

  const save = () => {
    const newSectionsState = reportPages[pageIndex].sections
      .map((item, i) => {
        if (i === sectionIndex) {
          return {
            ...item,
            name: newSectionName,
          };
        }
        return item;
      });
    const newPageState = reportPages.map((item, i) => {
      if (i === pageIndex) {
        return { ...item, sections: newSectionsState };
      }
      return item;
    });
    setReportPages(newPageState);
    setEditModal(false);
  };

  const resolveReportType = (): string | undefined => {
    if (builderMode || preview) {
      let forType: number | undefined;
      if (reportForType !== null && reportForType !== undefined) {
        forType = reportForType;
      } else if (page.forAssessmentType !== null && page.forAssessmentType !== undefined) {
        forType = page.forAssessmentType;
      } else {
        forType = section.forAssessmentType;
      }
      const type = reportAvailableTypes.find((x) => x.value === (forType ?? null));
      if (type) {
        return ` (${type.key})`;
      }
    }

    return undefined;
  };

  return (
    <>
      <div id={`reportSection-${sectionIndex}`} className="report-section">
        <div className="report-section-header">
          <span>
            {section.name}
            {resolveReportType()}
          </span>
          {builderMode && (
            <>
              <span className="text-nowrap">
                <FontAwesomeIcon icon={['fas', 'pencil-alt']} className="mr-2" style={{ cursor: 'pointer' }} onClick={() => toggleEditModal()} />
                <FontAwesomeIcon
                  icon={['fas', 'trash-alt']}
                  className="mr-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleDeleteModal()}
                />
                <span {...dragHandleProps}><FontAwesomeIcon icon={['fas', 'arrows-alt']} /></span>
              </span>
              <PromptModal headerText="Delete section" confirmAction={() => deleteSection()} isOpen={deleteModal} toggle={toggleDeleteModal}>
                Are you sure you want to delete this section?
              </PromptModal>
            </>
          )}
        </div>
        {!builderMode && (
          <div className="report-section-content">
            {/* TEMP: custom logic for generosity.impact.app, cbchighlands.impact.app [OrganizationID]=55401, 55478 */}
            {assessmentConfiguration.organizationID && [55401.55478].indexOf(assessmentConfiguration.organizationID) > -1 && section.id && section.id < 0 ? '' : <AssessmentType />}
            <AssessmentGraph location={`report-section-${section.id}`} />
          </div>
        )}
      </div>
      {builderMode && (
        <Modal backdrop="static" size="lg" isOpen={editModal} toggle={toggleEditModal}>
          <ModalHeader toggle={toggleEditModal}>
            Edit
            {' '}
            {section?.name}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Label for="sectionName" sm={2}>Title</Label>
              <Col sm={10}>
                <Input
                  type="text"
                  name="name"
                  id="sectionName"
                  placeholder="Enter section title"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                />
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => deleteSection()}>Delete Section</Button>
            {' '}
            <Button color="primary" onClick={() => save()}>Save Section</Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

AssessmentResult.defaultProps = {
  dragHandleProps: undefined,
  builderMode: false,
  preview: false,
};

export default AssessmentResult;
